import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { notesApi } from '@/lib/api';
import { useLanguage } from '@/context/LanguageContext';
import { toast } from 'sonner';
import { 
  Search, Filter, Trash2, Calendar, Mic, PenSquare,
  Loader2, ChevronDown, Video, Edit3, Check, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export default function NotesPage() {
  const { t } = useLanguage();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [deleteId, setDeleteId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editMood, setEditMood] = useState([5]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchNotes();
  }, [filter]);

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== 'all') {
        params.type = filter;
      }
      const response = await notesApi.getAll(params);
      setNotes(response.data);
    } catch (error) {
      toast.error('Failed to fetch notes');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await notesApi.delete(deleteId);
      setNotes(notes.filter(n => n.id !== deleteId));
      toast.success('Note deleted');
    } catch (error) {
      toast.error('Failed to delete note');
    } finally {
      setDeleteId(null);
    }
  };

  const startEdit = (note) => {
    setEditingId(note.id);
    setEditContent(note.content || note.transcript || '');
    setEditMood([note.mood || 5]);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditContent('');
    setEditMood([5]);
  };

  const saveEdit = async (noteId) => {
    setSaving(true);
    try {
      const response = await notesApi.update(noteId, {
        content: editContent,
        mood: editMood[0]
      });
      setNotes(notes.map(n => n.id === noteId ? response.data : n));
      setEditingId(null);
      toast.success('Note updated');
    } catch (error) {
      toast.error('Failed to update note');
    } finally {
      setSaving(false);
    }
  };

  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case 'positive': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'negative': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
    }
  };

  const filteredNotes = notes.filter(note => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      note.content?.toLowerCase().includes(searchLower) ||
      note.transcript?.toLowerCase().includes(searchLower) ||
      note.tags?.some(tag => tag.toLowerCase().includes(searchLower))
    );
  });

  return (
    <div className="space-y-6" data-testid="notes-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl text-white mb-1">{t('notes_title')}</h1>
          <p className="text-slate-400">{filteredNotes.length} {t('notes_entries')}</p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
          <Input
            placeholder={t('notes_search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-navy-900 border-white/10 text-white placeholder:text-slate-500"
            data-testid="search-input"
          />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="border-white/10 text-slate-300" data-testid="filter-btn">
              <Filter size={18} className="mr-2" />
              {filter === 'all' ? t('notes_all_types') : filter === 'text' ? t('notes_text') : filter === 'video' ? t('notes_video') : t('notes_voice')}
              <ChevronDown size={16} className="ml-2" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-navy-900 border-white/10">
            <DropdownMenuItem onClick={() => setFilter('all')} className="text-slate-300 focus:text-white focus:bg-white/10">{t('notes_all_types')}</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setFilter('text')} className="text-slate-300 focus:text-white focus:bg-white/10"><PenSquare size={16} className="mr-2" /> {t('notes_text')}</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setFilter('audio')} className="text-slate-300 focus:text-white focus:bg-white/10"><Mic size={16} className="mr-2" /> {t('notes_voice')}</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setFilter('video')} className="text-slate-300 focus:text-white focus:bg-white/10"><Video size={16} className="mr-2" /> {t('notes_video')}</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="animate-spin text-cyan" size={32} />
        </div>
      ) : filteredNotes.length === 0 ? (
        <Card className="bg-navy-900/40 border-white/5">
          <CardContent className="p-12 text-center">
            <PenSquare className="mx-auto text-slate-600 mb-4" size={48} />
            <h3 className="text-white text-xl font-medium mb-2">{t('notes_empty_title')}</h3>
            <p className="text-slate-400">{search ? t('notes_empty_search') : t('notes_empty_desc')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <AnimatePresence>
            {filteredNotes.map((note, index) => (
              <motion.div
                key={note.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="bg-navy-900/40 border-white/5 hover:border-cyan/20 transition-colors">
                  <CardContent className="p-5">
                    {editingId === note.id ? (
                      <div className="space-y-4" data-testid={`edit-note-form-${note.id}`}>
                        <Textarea
                          value={editContent}
                          onChange={(e) => setEditContent(e.target.value)}
                          className="min-h-[100px] bg-navy-950 border-white/10 text-white resize-none"
                          data-testid="edit-note-content"
                        />
                        <div className="flex items-center gap-4">
                          <span className="text-slate-400 text-sm">Mood: {editMood[0]}/10</span>
                          <Slider value={editMood} onValueChange={setEditMood} max={10} min={1} step={1} className="flex-1 max-w-xs" data-testid="edit-note-mood" />
                        </div>
                        <div className="flex gap-2">
                          <Button onClick={() => saveEdit(note.id)} disabled={saving} className="bg-cyan text-navy-950 hover:bg-cyan-300 rounded-full px-6" data-testid="save-edit-btn">
                            {saving ? <Loader2 className="animate-spin" size={16} /> : <><Check size={16} className="mr-1" /> Save</>}
                          </Button>
                          <Button variant="ghost" onClick={cancelEdit} className="text-slate-400 hover:text-white rounded-full" data-testid="cancel-edit-btn">
                            <X size={16} className="mr-1" /> Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            {note.type === 'audio' ? (
                              <Mic className="text-gold" size={16} />
                            ) : note.type === 'video' ? (
                              <Video className="text-purple-400" size={16} />
                            ) : (
                              <PenSquare className="text-cyan" size={16} />
                            )}
                            <span className="text-xs text-slate-500 uppercase tracking-wider">
                              {note.type === 'audio' ? t('notes_voice_note') : note.type === 'video' ? t('notes_video_note') : t('notes_text_entry')}
                            </span>
                            {note.mood && (
                              <span className="text-xs text-slate-400 ml-2">
                                {t('notes_mood')}: {note.mood}/10
                              </span>
                            )}
                          </div>
                          <p className="text-slate-200 mb-3">{note.content || note.transcript}</p>
                          {note.summary && note.summary !== note.content && (
                            <p className="text-slate-400 text-sm mb-3 italic">{t('notes_summary')}: {note.summary}</p>
                          )}
                          <div className="flex flex-wrap items-center gap-2">
                            {note.sentiment && (
                              <span className={`text-xs px-2 py-1 rounded-full border ${getSentimentColor(note.sentiment)}`}>{note.sentiment}</span>
                            )}
                            {note.tags?.map(tag => (
                              <span key={tag} className="text-xs px-2 py-1 rounded-full bg-navy-800 text-slate-300 border border-white/5">#{tag}</span>
                            ))}
                            <span className="text-xs text-slate-500 flex items-center gap-1 ml-auto">
                              <Calendar size={12} />
                              {new Date(note.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-col gap-1 flex-shrink-0">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => startEdit(note)}
                            className="text-slate-500 hover:text-cyan hover:bg-cyan/10"
                            data-testid={`edit-note-${note.id}`}
                          >
                            <Edit3 size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleteId(note.id)}
                            className="text-slate-500 hover:text-red-400 hover:bg-red-400/10"
                            data-testid={`delete-note-${note.id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-navy-900 border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">{t('notes_delete_title')}</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">{t('notes_delete_desc')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-navy-800 border-white/10 text-slate-300 hover:bg-navy-700">{t('notes_cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 text-white hover:bg-red-600">{t('notes_delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
